import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { API } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { competenceLabel } from '../lib/format';
import axios from 'axios';

export default function ImportarFluxo() {
  const { companies, competence, loadAll } = useApp();
  const [companyId, setCompanyId] = useState('');
  const [comp, setComp] = useState(competence);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    window.open(`${API}/cashflow/template`, '_blank');
    toast.success('Template padrão baixado!');
  };

  const downloadCoursesTemplate = () => {
    window.open(`${API}/cashflow/template-courses`, '_blank');
    toast.success('Template de cursos baixado!');
  };

  const [importMode, setImportMode] = useState('standard'); // 'standard' ou 'courses'

  const handleUpload = async () => {
    if (!companyId) return toast.error('Selecione a empresa');
    if (!file) return toast.error('Selecione a planilha (.xlsx)');
    if (!/^\d{4}-\d{2}$/.test(comp)) return toast.error('Competência inválida (use YYYY-MM)');

    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', companyId);
      fd.append('competence', comp);
      fd.append('replace', replace);
      const endpoint = importMode === 'courses' ? '/cashflow/upload-courses' : '/cashflow/upload';
      const { data } = await axios.post(`${API}${endpoint}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast.success(`Importado: ${data.created_revenues} entradas + ${data.created_expenses} saídas`);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fade-in">
      <TopBar
        title="Importar Fluxo de Caixa"
        subtitle="Suba a planilha de fluxo de caixa de cada empresa do grupo"
      />
      <div className="px-8 py-7 space-y-5 max-w-5xl">

        <SectionCard title="1️⃣ Baixe o template padrão" subtitle="Existem 2 modelos disponíveis - escolha conforme o tipo de operação da empresa">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-zinc-800 rounded-md p-4 bg-zinc-900/40">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-zinc-900 border border-zinc-800 shrink-0">
                  <FileSpreadsheet size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-medium text-zinc-100">Fluxo Padrão</div>
                  <div className="text-[11px] text-zinc-500">Lícito.Guru · Consultoria · Representações · & Lopes</div>
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                Colunas: Data, Descrição, Categoria, Centro de Custo, Tipo (CUSTO/DESPESA/INV/IMPOSTO), Previsão, Realizado, Status.
              </p>
              <Button
                onClick={downloadTemplate}
                className="btn-gold gap-2 mt-3 w-full"
                size="sm"
                data-testid="btn-download-template"
              >
                <Download size={14} /> Baixar Template Padrão
              </Button>
            </div>
            <div className="border border-[#D4AF37]/40 rounded-md p-4 bg-amber-950/10">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 shrink-0">
                  <FileSpreadsheet size={18} className="text-[#D4AF37]" />
                </div>
                <div>
                  <div className="font-medium text-zinc-100">Fluxo de Cursos & Eventos</div>
                  <div className="text-[11px] text-zinc-500">Legislativo · & Lopes · Licitações · Pregoeiros</div>
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                <strong className="gold-text">Inclui coluna "Curso/Evento"</strong> + inscritos para cálculo automático de ticket médio, custo/aluno e lucro por curso.
              </p>
              <Button
                onClick={downloadCoursesTemplate}
                className="btn-gold gap-2 mt-3 w-full"
                size="sm"
                data-testid="btn-download-courses-template"
              >
                <Download size={14} /> Baixar Template de Cursos
              </Button>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="2️⃣ Faça o upload da planilha preenchida">
          <div className="flex items-center gap-2 mb-4">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Tipo de planilha:</Label>
            <button
              onClick={() => setImportMode('standard')}
              className={`px-3 py-1.5 text-xs rounded border transition-all ${importMode === 'standard' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
              data-testid="mode-standard"
            >
              Fluxo Padrão
            </button>
            <button
              onClick={() => setImportMode('courses')}
              className={`px-3 py-1.5 text-xs rounded border transition-all ${importMode === 'courses' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
              data-testid="mode-courses"
            >
              Fluxo de Cursos
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa *</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1" data-testid="input-import-company">
                  <SelectValue placeholder="Selecione a empresa..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="focus:bg-zinc-800">
                      <span className="w-2 h-2 rounded-full mr-2 inline-block" style={{ background: c.color }} />
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Competência (YYYY-MM)</Label>
              <Input
                value={comp}
                onChange={(e) => setComp(e.target.value)}
                placeholder="2026-05"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 font-mono"
                data-testid="input-import-competence"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                {comp && /^\d{4}-\d{2}$/.test(comp) ? competenceLabel(comp) : 'Use formato YYYY-MM'}
              </p>
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Planilha (.xlsx) *</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 file:bg-zinc-800 file:text-zinc-100 file:border-0 file:rounded file:px-3 file:mr-3 cursor-pointer"
                data-testid="input-import-file"
              />
              {file && (
                <p className="text-[11px] text-emerald-400 mt-1">
                  <CheckCircle2 size={11} className="inline mr-1" />
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                id="replace"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
                className="rounded border-zinc-700 bg-zinc-900 text-[#D4AF37]"
                data-testid="checkbox-replace"
              />
              <label htmlFor="replace" className="text-sm text-zinc-300 cursor-pointer">
                Substituir lançamentos existentes desta empresa nesta competência
              </label>
              {replace && (
                <span className="pill pill-warning ml-2">
                  <AlertTriangle size={11} /> Apagará dados existentes
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={handleUpload}
            disabled={uploading || !companyId || !file}
            className="btn-gold gap-2 mt-5"
            data-testid="btn-upload-cashflow"
          >
            <Upload size={15} />
            {uploading ? 'Importando...' : 'Importar Fluxo de Caixa'}
          </Button>
        </SectionCard>

        {result && (
          <SectionCard title="✅ Resultado da Importação">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="overline text-zinc-500">Empresa</div>
                <div className="text-zinc-100 mt-1 font-medium">{result.company}</div>
              </div>
              <div>
                <div className="overline text-zinc-500">Competência</div>
                <div className="text-zinc-100 mt-1 font-mono">{competenceLabel(result.competence)}</div>
              </div>
              <div>
                <div className="overline text-zinc-500">Lançamentos Criados</div>
                <div className="text-zinc-100 mt-1 font-mono">
                  <span className="emerald-text">{result.created_revenues}</span> entradas +{' '}
                  <span className="red-text">{result.created_expenses}</span> saídas
                </div>
              </div>
            </div>
          </SectionCard>
        )}

        <SectionCard title="ℹ️ Como funciona">
          <div className="space-y-2 text-sm text-zinc-300">
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span>O sistema lê a planilha automaticamente, identifica as seções de ENTRADAS e SAÍDAS, e cria lançamentos individuais no sistema.</span>
            </div>
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span>Categorias e centros de custo novos são cadastrados automaticamente conforme aparecerem na planilha.</span>
            </div>
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span>Após importar, os valores aparecem imediatamente no Dashboard, DRE, Fluxo de Caixa e Consolidado do grupo.</span>
            </div>
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <span>Para fluxo de caixa <strong className="gold-text">provisionado</strong> (próximo mês), use as páginas <strong>Contas a Pagar</strong> e <strong>Contas a Receber</strong> ou inclua linhas com status PREVISTO na planilha.</span>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
